import type { Markup } from '@/types';
import type { Media, MediaItem } from '@/types/schemas';

/**
 * A markup UPDATE's `media` is never the whole map (image markup spec 3.2, "Why a map, not a list"; I3a Deviation 5):
 * it is a PER-ITEM PATCH -- item id -> the item to write, or null to remove it; unlisted items are untouched. The
 * remote backends write it as dotted paths (services/markupFields.ts withMediaPaths); every in-memory and local
 * store merges it here ({@link applyMediaPatch}); an undo writes its inverse ({@link inverseMediaPatch}). So a
 * whole-map write never happens on any path, and a concurrent add by another editor is never replaced.
 */
export type MediaPatch = Record<string, MediaItem | null>;

const mediaOf = (m: Partial<Markup> | undefined): Media | undefined => (m as { media?: Media } | undefined)?.media;

/** `media` with `patch` applied item by item; undefined when nothing is left (a markup without photos has no map). */
export function applyMediaPatch(media: Media | undefined, patch: MediaPatch): Media | undefined {
    const out: Media = { ...(media ?? {}) };
    for (const [id, item] of Object.entries(patch)) {
        if (item === null) delete out[id];
        else out[id] = item;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}

/** The patch that undoes `patch` on `media`: each listed item back to what it was (absent -> null, i.e. removed). */
export function inverseMediaPatch(media: Media | undefined, patch: MediaPatch): MediaPatch {
    const inverse: MediaPatch = {};
    for (const id of Object.keys(patch)) inverse[id] = media?.[id] ?? null;
    return inverse;
}

/** The undo recorder's "before" for an update: each key's current value, `media` as the inverse per-item patch. */
export function updateBeforeState(existing: Markup, updates: Partial<Markup>): Partial<Markup> {
    const before: Record<string, unknown> = {};
    for (const key of Object.keys(updates)) {
        before[key] = key === 'media'
            ? inverseMediaPatch(mediaOf(existing), (updates as { media?: MediaPatch }).media ?? {})
            : existing[key as keyof Markup];
    }
    return before as Partial<Markup>;
}

/**
 * Two consecutive update records of one markup merged into one undo step (the undo manager's merge window):
 * `after` = the later write wins per key, `media` per item; `before` = the EARLIER record's value for every key it
 * holds (the state before the step), the later record's for keys only it touched -- per item for `media` too, so an
 * undo of "add photo, then type its caption" removes the photo.
 */
export function mergeUpdateStates(
    first: { beforeState: Partial<Markup>; afterState: Partial<Markup> },
    second: { beforeState: Partial<Markup>; afterState: Partial<Markup> },
): { beforeState: Partial<Markup>; afterState: Partial<Markup> } {
    const after: Record<string, unknown> = { ...first.afterState, ...second.afterState };
    const before: Record<string, unknown> = { ...second.beforeState, ...first.beforeState };
    const a1 = (first.afterState as { media?: MediaPatch }).media;
    const a2 = (second.afterState as { media?: MediaPatch }).media;
    if (a1 && a2) after.media = { ...a1, ...a2 };
    const b1 = (first.beforeState as { media?: MediaPatch }).media;
    const b2 = (second.beforeState as { media?: MediaPatch }).media;
    if (b1 && b2) before.media = { ...b2, ...b1 };
    return { beforeState: before as Partial<Markup>, afterState: after as Partial<Markup> };
}
